import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../stateManagement/store';
import {
    fetchUserOrganizationsList,
    selectUserOrganizationsList,
    selectUserOrganizationsListLoading,
    deactivateOrganizationAsync,
    activateOrganizationAsync,
    createOrganizationAsync,
    deleteOrganizationAsync,
} from '../../stateManagement/organizationUserSlice';
import { setSelectedOrganization } from '../../stateManagement/selectedOrganizationSlice';
import { removeTarget } from '../../stateManagement/targetSlice';
import GridTable, { GridColumnType } from '../../gridTable/GridTable';
// import { IOrganization, IOrganizationRequest, IOrganizationResponse } from '../../interfaces/IOrganization';
import OrganizationDialog, { organizationSchema } from './OrganizationDialog';
import { z } from 'zod';
import { useSnackbar } from 'notistack';
import AuthService from '../../services/AuthService';


export interface IOrganization {
    organizationId?: string;
    name?: string;
    country?: string;
    provinceOrState?: string;
    city?: string;
    address?: string;
    phone?: string;
    website?: string;
    email?: string;
    defaultTaxPercent?: number;
}

export interface IOrganizationRequest {
    name: string;
    country?: string;
    provinceOrState?: string;
    city?: string;
    address?: string;
    phone?: string;
    website?: string;
    email?: string;
    defaultTaxPercent?: number;
}

export interface IUserOrganization {
    userId?: string;
    organizationId?: string;
    role?: string;
}

export interface IOrganizationResponse {
    userId?: string;
    organizationId: string;
    organizationName?: string;
    name?: string;
    organizationIsActivated?: boolean;
    organizationIsDeleted?: boolean;
    userRole?: string;
}

const OrganizationList: React.FC = () => {

    const organizationUsers = useSelector(selectUserOrganizationsList);

    const organizationType: GridColumnType<IOrganizationResponse> = {
        organizationId: { type: "Id", dontShow: true },
        organizationName: { type: "Text", header: 'Name', dontShow: true, },
        name: { type: "Text", header: 'Name', showInCreateRow: true },
        userRole: { type: "Text", header: 'Rule' },
        userId: { type: "Id", dontShow: true },
    };

    const loading = useSelector(selectUserOrganizationsListLoading);

    const dispatch = useDispatch<AppDispatch>();

    const [rowLoading, setRowLoading] = useState<string[]>([]);

    const handleBack = () => {
        dispatch(removeTarget());
    };

    const handleSelect = (input: IOrganizationResponse) => {
        dispatch(setSelectedOrganization(input));
    }

    const deactivate = async (input: IOrganizationResponse) => {

        if (input.organizationId) {
            setRowLoading([...rowLoading, input.organizationId])
            dispatch(deactivateOrganizationAsync(input.organizationId!))
                .catch((error) => {
                    console.error(error, { variant: 'error' });
                })
                .finally(() => {
                    setRowLoading(rowLoading.filter((item) => item !== input.organizationId))
                });
        }
    };

    const activate = async (input: IOrganizationResponse) => {
        if (input.organizationId) {
            setRowLoading([...rowLoading, input.organizationId])
            dispatch(activateOrganizationAsync(input.organizationId!))
                .unwrap()
                .catch((error) => {
                    console.error(error, { variant: 'error' });
                })
                .finally(() => {
                    setRowLoading(rowLoading.filter((item) => item !== input.organizationId))
                });
        }
    };

    const handleDelete = async (input: IOrganizationResponse) => {

        if (input.organizationId) {
            setRowLoading([...rowLoading, input.organizationId]);
            dispatch(deleteOrganizationAsync(input.organizationId!))
                .unwrap()
                .catch((error) => {
                    console.error(error, { variant: 'error' });
                })
                .finally(() => {
                    setRowLoading(rowLoading.filter((item) => item !== input.organizationId))
                });
        }

        //if user isnt owner it shouls delete userorganization , organizationUser (or remove member)
    };


    const { enqueueSnackbar } = useSnackbar();

    const [createRowDto, setCreateRowDto] = useState<IOrganizationRequest>({ name: '' });
    const [createRowLoading, setCreateRowLoading] = useState(false);

    const handleSave = async (data: IOrganizationRequest) => {

        try {
            const validated = organizationSchema.parse(data);
            setCreateRowLoading(true);
            dispatch(createOrganizationAsync(validated))
                .catch((error) => {
                    console.error(error, { variant: 'error' });
                })
                .finally(() => {
                    setCreateRowDto({ name: '' });
                    setCreateRowLoading(false);
                });
        } catch (error) {
            if (error instanceof z.ZodError) {
                error.issues.map(item => {
                    enqueueSnackbar(item.message, { variant: 'error' })
                }
                );
            }
        }
    };

    const [organizationDialogState, setOrganizationDialogState] = useState<{ open: boolean; organizationInput?: IOrganizationResponse; }>({ open: false });


    const handleFormOpen = () => {
        setOrganizationDialogState({ open: true })
    }

    const [showDeleted, setShowDeleted] = useState<boolean>(false);
    const onClickDeleteFolder = () => {
        setShowDeleted(!showDeleted);
    }

    const closeOrganizationDialog = (input?: IOrganization) => {
        setOrganizationDialogState({ open: false, organizationInput: undefined })
    }

    const handleEdit = async (input: IOrganizationResponse) => {
        setOrganizationDialogState({ open: true, organizationInput: input })
    };

    useEffect(() => {
        if (!AuthService.isTokenExpired()) {
            dispatch(fetchUserOrganizationsList(showDeleted));
        }
    }, [dispatch, showDeleted]);

    return (
        <>
            <GridTable
                gridTitle='Place list'
                gridTitleInfo='In below list select place to manage its service providers, service provider availabilities, appointments, clients, services and ...'
                loading={loading || createRowLoading}
                list={organizationUsers.map((item) => ({ ...item, id: item.organizationId, name: item.organizationName }))}
                inputType={organizationType}
                onClickRow={handleSelect}
                onClickRowInfo={'Select place to see its service providers'}
                activateSelectableByField='organizationIsActivated'
                moreRowTools={[
                    { buttonTitle: 'Deactivate', onClick: deactivate, activateByField: 'organizationIsActivated' },
                    { buttonTitle: 'Activate', onClick: activate, deactivateByField: 'organizationIsActivated' },
                ]}
                onClickNew={handleFormOpen}
                onEditRow={handleEdit}
                // onClickBack={handleBack}
                onClickBackInfo='Back to main menu'
                onDeleteRow={handleDelete}
                rowLoading={rowLoading}
                createRowDto={createRowDto}
                setCreateRowDto={setCreateRowDto}
                handleCreateRow={handleSave}
                onClickDeleteFolder={onClickDeleteFolder}
                showDeleted={showDeleted}
            />
            <OrganizationDialog state={organizationDialogState} closeOrganizationDialog={closeOrganizationDialog} />
        </>
    );
};

export default OrganizationList;